import React from 'react';

const Stats = ({ stats, onRefresh }) => {
  if (!stats) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        جاري التحميل...
      </div>
    );
  }

  const formatNumber = (num) => {
    if (typeof num !== 'number') return num;
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getPackageLabel = (pkg) => {
    const packages = {
      basic: 'بيسك',
      standard: 'ستاندرد',
      premium: 'بريميوم',
    };
    return packages[pkg] || pkg;
  };

  const getStatusLabel = (status) => {
    const statuses = {
      pending: 'قيد الانتظار',
      contacted: 'تم التواصل',
      converted: 'تم التحويل',
      rejected: 'مرفوض',
      active: 'نشط',
      expired: 'منتهي',
      cancelled: 'ملغي',
    };
    return statuses[status] || status;
  };

  const getSourceLabel = (source) => {
    const sources = {
      'lead-generation': 'صفحة التسجيل',
      'initial-setup': 'إعداد أولي',
    };
    return sources[source] || source;
  };

  return (
    <div>
      {/* Customers Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">العملاء</h3>
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">إجمالي العملاء</div>
            <div className="stat-value">{stats.customers?.total || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">العملاء النشطون</div>
            <div className="stat-value">{stats.customers?.active || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">العملاء غير النشطين</div>
            <div className="stat-value">{stats.customers?.inactive || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">عملاء جدد (آخر 30 يوم)</div>
            <div className="stat-value">{stats.customers?.recent || 0}</div>
          </div>
        </div>
      </div>

      {/* Balances Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">الأرصدة</h3>
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">إجمالي رصيد AI</div>
            <div className="stat-value">{formatNumber(stats.balances?.totalAICredits || 0)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">إجمالي رصيد SMS</div>
            <div className="stat-value">{formatNumber(stats.balances?.totalSMSCredits || 0)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">إجمالي رصيد OpenAI</div>
            <div className="stat-value">{formatNumber(stats.balances?.totalOpenAIBalance || 0)}</div>
          </div>
        </div>
      </div>

      {/* Subscriptions Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">الاشتراكات</h3>
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">إجمالي الاشتراكات</div>
            <div className="stat-value">{stats.subscriptions?.total || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">اشتراكات نشطة</div>
            <div className="stat-value">{stats.subscriptions?.active || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">اشتراكات منتهية</div>
            <div className="stat-value">{stats.subscriptions?.expired || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">اشتراكات جديدة (آخر 30 يوم)</div>
            <div className="stat-value">{stats.subscriptions?.recent || 0}</div>
          </div>
        </div>
        {stats.subscriptions?.byPackage && stats.subscriptions.byPackage.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600, color: 'var(--gray-700)' }}>الاشتراكات حسب الباقة</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>الباقة</th>
                    <th>العدد</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.subscriptions.byPackage.map((item, index) => (
                    <tr key={index}>
                      <td>{getPackageLabel(item._id)}</td>
                      <td>{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {stats.subscriptions?.expiringSoon && stats.subscriptions.expiringSoon.length > 0 && (
          <div className="alert alert-warning" style={{ marginTop: '2rem' }}>
            <span>⚠️</span>
            <div>
              <strong>تحذير: اشتراكات ستنتهي خلال 30 يوم ({stats.subscriptions.expiringSoon.length})</strong>
            </div>
          </div>
        )}
        {stats.subscriptions?.expiringSoon && stats.subscriptions.expiringSoon.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>العميل</th>
                    <th>البريد الإلكتروني</th>
                    <th>الباقة</th>
                    <th>تاريخ الانتهاء</th>
                    <th>الأيام المتبقية</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.subscriptions.expiringSoon.map((sub) => (
                    <tr key={sub._id}>
                      <td><strong>{sub.customerName}</strong></td>
                      <td>{sub.customerEmail}</td>
                      <td>{getPackageLabel(sub.package)}</td>
                      <td>{new Date(sub.endDate).toLocaleDateString('en-US')}</td>
                      <td>
                        <span className={`badge ${sub.daysRemaining <= 7 ? 'badge-danger' : sub.daysRemaining <= 14 ? 'badge-warning' : 'badge-info'}`}>
                          {sub.daysRemaining} يوم
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* VPS Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">خوادم VPS</h3>
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">إجمالي الخوادم</div>
            <div className="stat-value">{stats.vps?.total || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">خوادم نشطة</div>
            <div className="stat-value">{stats.vps?.active || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">خوادم غير نشطة</div>
            <div className="stat-value">{stats.vps?.inactive || 0}</div>
          </div>
        </div>
        {stats.vps?.expiringSoon && stats.vps.expiringSoon.length > 0 && (
          <div className="alert alert-warning" style={{ marginTop: '2rem' }}>
            <span>⚠️</span>
            <div>
              <strong>تحذير: خوادم VPS ستنتهي خلال 30 يوم ({stats.vps.expiringSoon.length})</strong>
            </div>
          </div>
        )}
        {stats.vps?.expiringSoon && stats.vps.expiringSoon.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>IP</th>
                    <th>المزود</th>
                    <th>تاريخ الانتهاء</th>
                    <th>الأيام المتبقية</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.vps.expiringSoon.map((vps) => (
                    <tr key={vps._id}>
                      <td><strong>{vps.ip}</strong></td>
                      <td>{vps.provider}</td>
                      <td>{new Date(vps.endDate).toLocaleDateString('en-US')}</td>
                      <td>
                        <span className={`badge ${vps.daysRemaining <= 7 ? 'badge-danger' : vps.daysRemaining <= 14 ? 'badge-warning' : 'badge-info'}`}>
                          {vps.daysRemaining} يوم
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Leads Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">الطلبات</h3>
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">إجمالي الطلبات</div>
            <div className="stat-value">{stats.leads?.total || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">طلبات جديدة (آخر 30 يوم)</div>
            <div className="stat-value">{stats.leads?.recent || 0}</div>
          </div>
        </div>
        {stats.leads?.byStatus && stats.leads.byStatus.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600, color: 'var(--gray-700)' }}>الطلبات حسب الحالة</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>الحالة</th>
                    <th>العدد</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.leads.byStatus.map((item, index) => (
                    <tr key={index}>
                      <td>{getStatusLabel(item._id)}</td>
                      <td>{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {stats.leads?.bySource && stats.leads.bySource.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600, color: 'var(--gray-700)' }}>الطلبات حسب المصدر</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>المصدر</th>
                    <th>العدد</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.leads.bySource.map((item, index) => (
                    <tr key={index}>
                      <td>{getSourceLabel(item._id)}</td>
                      <td>{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Transactions Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">المعاملات</h3>
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">إجمالي المعاملات</div>
            <div className="stat-value">{stats.transactions?.total || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">عمليات خصم</div>
            <div className="stat-value">{stats.transactions?.debits || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">عمليات إضافة</div>
            <div className="stat-value">{stats.transactions?.credits || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">إجمالي المبلغ المخصوم</div>
            <div className="stat-value">{formatNumber(stats.transactions?.totalDebitAmount || 0)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">إجمالي المبلغ المضاف</div>
            <div className="stat-value">{formatNumber(stats.transactions?.totalCreditAmount || 0)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">معاملات جديدة (آخر 30 يوم)</div>
            <div className="stat-value">{stats.transactions?.recent || 0}</div>
          </div>
        </div>
        {stats.transactions?.byService && stats.transactions.byService.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600, color: 'var(--gray-700)' }}>المعاملات حسب الخدمة</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>الخدمة</th>
                    <th>العدد</th>
                    <th>إجمالي المبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.transactions.byService.map((item, index) => (
                    <tr key={index}>
                      <td>
                        {item._id === 'AI_IMAGE' ? 'توليد الصور' : 
                         item._id === 'AI_IMAGE_PRO' ? 'توليد الصور Pro' :
                         item._id === 'SMS' ? 'الرسائل النصية' : 
                         item._id === 'OPENAI' ? 'OpenAI' :
                         item._id === 'MANUAL' ? 'يدوي' : item._id}
                      </td>
                      <td>{item.count}</td>
                      <td>{formatNumber(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Usage Logs Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">سجلات الاستخدام</h3>
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">إجمالي السجلات</div>
            <div className="stat-value">{stats.usageLogs?.total || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">سجلات ناجحة</div>
            <div className="stat-value">{stats.usageLogs?.successful || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">سجلات فاشلة</div>
            <div className="stat-value">{stats.usageLogs?.failed || 0}</div>
          </div>
        </div>
        {stats.usageLogs?.byService && stats.usageLogs.byService.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600, color: 'var(--gray-700)' }}>السجلات حسب الخدمة</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>الخدمة</th>
                    <th>العدد</th>
                    <th>إجمالي التكلفة</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.usageLogs.byService.map((item, index) => (
                    <tr key={index}>
                      <td>
                        {item._id === 'AI_IMAGE' ? 'توليد الصور' : 
                         item._id === 'AI_IMAGE_PRO' ? 'توليد الصور Pro' :
                         item._id === 'SMS' ? 'الرسائل النصية' : 
                         item._id === 'OPENAI' ? 'OpenAI' : item._id}
                      </td>
                      <td>{item.count}</td>
                      <td>{formatNumber(item.totalCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Usage by Service (from Transactions) */}
      {stats.usage && stats.usage.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">الاستخدام حسب الخدمة (من المعاملات)</h3>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>الخدمة</th>
                  <th>إجمالي الاستخدام</th>
                </tr>
              </thead>
              <tbody>
                {stats.usage.map((item, index) => (
                  <tr key={index}>
                    <td>
                      {item._id === 'AI_IMAGE' ? 'توليد الصور' : 
                       item._id === 'AI_IMAGE_PRO' ? 'توليد الصور Pro' :
                       item._id === 'SMS' ? 'الرسائل النصية' : 
                       item._id === 'OPENAI' ? 'OpenAI' : item._id}
                    </td>
                    <td>{formatNumber(item.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stats;
